import React, { useMemo, useEffect } from 'react';
import { View, Pressable, StyleSheet, ActivityIndicator, AppState } from 'react-native';
import Icon from '@/components/Icon';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useToastStore, useChatStore } from '@/store';
import { getStatusColor } from '@/constants/utils/files';
import moment from 'moment-mini';
import Text from '@/components/Typography/Text';
import { useIsFocused } from '@react-navigation/native';

const createStyles = (colors) => StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 280,
    width: '100%',
    padding: 10,
    borderRadius: 12,
    backgroundColor: colors.solid_color_tertiary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    position: 'relative',
    // shadowColor: '#000',
    // shadowOpacity: 0.05,
    // shadowRadius: 6,
    // shadowOffset: { width: 0, height: 2 },
  },
  info: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  iconWrapper: {
    width: 18,
    height: 18,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  meta: {
    fontSize: 11,
    opacity: 0.8,
  },
  chip: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    borderWidth: 1,
    marginLeft: 'auto',
  },
  chipLabel: {
    fontSize: 11,
    textTransform: 'capitalize',
  },
  progressBar: {
    height: 6,
    borderRadius: 4,
    overflow: 'hidden',
    width: '100%',
    marginTop: 4,
    backgroundColor: colors.solid_color_secondary,
  },
  progressInner: {
    height: '100%',
    borderRadius: 4,
    backgroundColor: colors.accent_color_primary,
  },
  button: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: colors.solid_color_secondary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  buttonText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.text_color_primary,
  }
});

function MessageInferenceJob({
  conversationId,
  messageId,
  inferenceId,
  job,
  onReload,
}) {
  const { colors } = useTheme();
  const { addToast } = useToastStore(state => state.actions);
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [isReloading, setIsReloading] = React.useState(false);
  const isFocused = useIsFocused();
  const [isAppActive, setIsAppActive] = React.useState(AppState.currentState === 'active');

  useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      setIsAppActive(state === 'active');
    });
    return () => {
      sub.remove();
    };
  }, []);
  
  const effectiveStatus = useMemo(() => {
    try {
      const createdAt = job?.createdAt ? moment(job.createdAt) : null;
      const isOverHour = createdAt ? moment().diff(createdAt, 'hours') > 1 : false;
      return (isOverHour ? 'failed' : (job?.status || 'pending')).toLowerCase();
    } catch (e) {
      return (job?.status || 'pending').toLowerCase();
    }
  }, [job?.createdAt, job?.status]);

  const isFailed = useMemo(() => ['failed', 'canceled', 'cancelled'].includes(effectiveStatus), [effectiveStatus]);
  const isCompleted = effectiveStatus === 'completed';
  const isSpinning = (!isFailed && !isCompleted) || isReloading;

  const handleReload = async () => {
    try {
      setIsReloading(true);
      if (typeof onReload === 'function') {
        await onReload({ conversationId, messageId, inferenceId, jobId: job?.id });
        return;
      }

      if (!conversationId || !messageId || !inferenceId || !job?.id) {
        addToast('Missing job identifiers', 'error');
        return;
      }

      addToast('Refreshing job status...', 'default');
      await useChatStore.getState().pollInferenceJob(conversationId, messageId, inferenceId, job.id);
      addToast('Job status updated', 'success');
    } catch (e) {
      addToast('Failed to refresh job status', 'error');
    } finally {
      setIsReloading(false);
    }
  };

  // Poll the job status every 3 seconds while active (pending/in_progress/processing).
  // The server caches poll responses for 2 seconds, so polling faster is wasteful.
  // We use a ref-based guard to prevent rapid re-polling when the component
  // re-renders due to addChat() replacing the chat data on each poll response.
  const lastPollTimeRef = React.useRef(0);
  const POLL_INTERVAL_MS = 3000;

  useEffect(() => {
    const hasIds = !!conversationId && !!messageId && !!inferenceId && !!job?.id;
    if (!hasIds || isFailed || isCompleted || !isFocused || !isAppActive) {
      return undefined;
    }

    let isCancelled = false;
    const poll = async () => {
      // Throttle: skip if we polled less than POLL_INTERVAL_MS ago
      const now = Date.now();
      if (now - lastPollTimeRef.current < POLL_INTERVAL_MS - 200) {
        return;
      }
      lastPollTimeRef.current = now;
      
      try {
        await useChatStore.getState().pollInferenceJob(conversationId, messageId, inferenceId, job.id);
      } catch (e) {
        // Silently ignore polling errors
      }
    };

    // Initial poll once when becoming active (throttled)
    poll();

    const intervalId = setInterval(() => {
      if (!isCancelled) poll();
    }, POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      clearInterval(intervalId);
    };
  }, [conversationId, messageId, inferenceId, job?.id, isFailed, isCompleted, isFocused, isAppActive]);

  const progressInnerDynamicStyle = useMemo(() => ({
    width: `${Math.max(0, Math.min(100, job?.progress ?? 0))}%`,
  }), [job?.progress]);

  const jobTypeRaw = job?.type || 'job';
  const jobType = String(jobTypeRaw).toLowerCase();
  const prettyJobType = jobType.charAt(0).toUpperCase() + jobType.slice(1);
  const statusText = isFailed
    ? `${prettyJobType} inference job failed`
    : isCompleted
      ? 'Inference job completed'
      : `Generating ${jobType}...`;
  const statusColors = useMemo(() => getStatusColor(effectiveStatus), [effectiveStatus]);

  return (
    <View style={styles.container}>
      <View style={styles.info}>
        <View style={styles.headerRow}>
          <View style={styles.titleRow}>
            <View style={styles.iconWrapper}>
              {isSpinning ? (
                <ActivityIndicator size="small" color={statusColors.text} />
              ) : (
                <Icon
                  name={isFailed ? 'triangle-exclamation' : 'check'}
                  size={14}
                  color={statusColors.text}
                />
              )}
            </View>
            <Text size='n' color='primary'>{statusText}</Text>
            <View style={[styles.chip, { backgroundColor: statusColors.background, borderColor: 'transparent' }]}> 
              <Text style={[styles.chipLabel, { color: statusColors.text }]}>{effectiveStatus.replace('_', ' ')}</Text>
            </View>
          </View>
        </View>
      {!!job?.error?.message && (
        <Text size='s' color='tertiary'>
          {job.error.message}
        </Text>
      )}
      {!isFailed && (
        <View style={[styles.progressBar, { backgroundColor: statusColors.background }]}>
          <View style={[styles.progressInner, { backgroundColor: statusColors.text }, progressInnerDynamicStyle]} />
        </View>
      )}
      </View>
    </View>
  );
}

MessageInferenceJob.propTypes = {
  conversationId: PropTypes.string.isRequired,
  messageId: PropTypes.string.isRequired,
  inferenceId: PropTypes.string,
  job: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    type: PropTypes.string,
    progress: PropTypes.number,
    updatedAt: PropTypes.string,
    createdAt: PropTypes.string,
    owner: PropTypes.shape({
      entityId: PropTypes.string,
      entityType: PropTypes.string,
    })
  }).isRequired,
  onReload: PropTypes.func,
};

export default React.memo(MessageInferenceJob);


